import { useEffect } from 'react';
import { apiFetch, v1Path } from '../../lib/api';
import { countLabel } from '../../lib/progress';
import {
  workItemSubtypeLabel,
  type MemberDto,
  type WorkItemFull,
  type WorkItemSubtypeDefinition,
} from '../../lib/work-types';
import { Button } from '../ui/button';
import BlockedToggle from './BlockedToggle';
import { ProgressMeter } from './ProgressParts';
import { LinkedWorkItemCard } from './WorkItemRelations';
import { useOverlays } from './overlay-context';

type Props = {
  item: WorkItemFull;
  projectId: string;
  readOnly?: boolean;
  onUpdated: (item: WorkItemFull) => void;
  subtypes?: WorkItemSubtypeDefinition[];
  members?: MemberDto[];
};

export default function WorkItemFamily({ item, projectId, readOnly, onUpdated, subtypes = [], members }: Props) {
  const parent = item.parent ?? null;
  const children = item.children ?? [];
  const showSubtasks = !readOnly || children.length > 0;
  const { openCreateWorkItem } = useOverlays();

  // The shared create screen saves the subtask and announces it; pick it up here.
  useEffect(() => {
    const reload = () => {
      void apiFetch<WorkItemFull>(v1Path(`/projects/${projectId}/work-items/${item.id}`)).then(onUpdated).catch(() => undefined);
    };
    window.addEventListener('bb:subtask-created', reload);
    return () => window.removeEventListener('bb:subtask-created', reload);
  }, [projectId, item.id, onUpdated]);

  if (!parent && !showSubtasks) return null;

  const rollup = item.rollup ?? null;
  const openByType = (rollup?.by_subtype ?? []).filter((entry) => entry.open > 0);

  async function refresh() {
    const fresh = await apiFetch<WorkItemFull>(v1Path(`/projects/${projectId}/work-items/${item.id}`));
    onUpdated(fresh);
    window.dispatchEvent(new CustomEvent('bb:work-items-changed'));
  }

  return (
    <section className="space-y-3">
      {parent ? (
        <div className="space-y-1.5">
          <h2 className="text-sm font-medium">Parent</h2>
          <LinkedWorkItemCard item={parent} />
        </div>
      ) : null}
      {showSubtasks ? (
        <div className="space-y-2">
          <div className="flex items-center gap-3">
            <h2 className="text-sm font-medium">Subtasks</h2>
            {rollup && rollup.total > 0 ? <ProgressMeter counts={rollup} className="max-w-56 flex-1" /> : null}
          </div>
          {openByType.length > 0 ? (
            <p className="text-xs text-muted-foreground">
              Open:{' '}
              {openByType
                .map((entry) => countLabel(entry.open, workItemSubtypeLabel(entry.key, subtypes)))
                .join(' · ')}
            </p>
          ) : null}
          {children.length > 0 ? (
            <ul className="space-y-1.5">
              {children.map((child) => (
                <li key={child.id} className="flex items-center gap-2">
                  {child.subtype ? (
                    <span className="w-20 shrink-0 truncate text-[11px] font-medium text-muted-foreground">
                      {workItemSubtypeLabel(child.subtype, subtypes)}
                    </span>
                  ) : (
                    <span className="w-20 shrink-0" aria-hidden />
                  )}
                  <LinkedWorkItemCard item={child} />
                  <BlockedToggle item={child} members={members} readOnly={readOnly} onChanged={refresh} />
                </li>
              ))}
            </ul>
          ) : null}
          {readOnly ? null : (
            <Button type="button" size="sm" variant="outline" onClick={() => openCreateWorkItem({ projectId, parentId: item.id })}>
              Add subtask
            </Button>
          )}
        </div>
      ) : null}
    </section>
  );
}
